import { useEffect } from "react";
import SettingsStore from "@/stores/SettingsStore";

function hexToHsl(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%`;
}

function setHslVar(root: HTMLElement, varName: string, hex: string | undefined) {
  if (hex) {
    const hsl = hexToHsl(hex);
    if (hsl) {
      root.style.setProperty(varName, hsl);
      return;
    }
  }
  root.style.removeProperty(varName);
}

export function useBranding() {
  const brandingPrimaryColor = SettingsStore(state => state.brandingPrimaryColor);
  const brandingCtaColor = SettingsStore(state => state.brandingCtaColor);
  const brandingAccentColor = SettingsStore(state => state.brandingAccentColor);
  const brandingTitle = SettingsStore(state => state.brandingTitle);
  const brandingFaviconUrl = SettingsStore(state => state.brandingFaviconUrl);
  const brandingUserMsgBg = SettingsStore(state => state.brandingUserMsgBg);
  const brandingBotMsgBg = SettingsStore(state => state.brandingBotMsgBg);
  const brandingUserMsgText = SettingsStore(state => state.brandingUserMsgText);
  const brandingBotMsgText = SettingsStore(state => state.brandingBotMsgText);

  useEffect(() => {
    const root = document.documentElement;

    setHslVar(root, "--primary", brandingPrimaryColor);
    setHslVar(root, "--cta", brandingCtaColor);
    setHslVar(root, "--accent", brandingAccentColor);

    const setOrRemove = (varName: string, value: string | undefined) => {
      if (value) {
        root.style.setProperty(varName, value);
      } else {
        root.style.removeProperty(varName);
      }
    };

    setOrRemove("--user-msg-bg", brandingUserMsgBg);
    setOrRemove("--bot-msg-bg", brandingBotMsgBg);
    setOrRemove("--user-msg-text", brandingUserMsgText);
    setOrRemove("--bot-msg-text", brandingBotMsgText);
  }, [brandingPrimaryColor, brandingCtaColor, brandingAccentColor, brandingUserMsgBg, brandingBotMsgBg, brandingUserMsgText, brandingBotMsgText]);

  useEffect(() => {
    if (brandingTitle) {
      document.title = brandingTitle;
    }
  }, [brandingTitle]);

  useEffect(() => {
    if (brandingFaviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = brandingFaviconUrl;
    }
  }, [brandingFaviconUrl]);
}
